import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeUnmount, type Component } from "vue";
import { storeToRefs } from "pinia";
import BasePageLayout from "@/components-UI/layouts/BasePageLayout.vue";
import BasePageTabs from "@/components-UI/BasePageTabs.vue";
import BaseStatus from "@/components-UI/BaseStatus.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import BaseSwitch from "@/components-UI/BaseSwitch.vue";
import SvgReload from "@/assets/icons/reload.svg";
import SvgTrash from "@/assets/icons/trash.svg";
import SvgPencil from "@/assets/icons/pencil.svg";
import SvgCancel from "@/assets/icons/reload.svg";
import SvgCheck from "@/assets/icons/check.svg";
import BaseAlert from "@/components-UI/BaseAlert.vue";
import { useEUAccountsStore } from '@/store_v3/EUAccountsStore';
import { showServerError, showNotification } from "@/utils";
import { USER_ROLES } from "@/mappings/user-roles";
import store from "@/store";
import router from "@/router";
import { translateEUAccountStatus } from "@/utils/translate";
import { IStatus } from "@/types";
import { createEUAccount, updateGoalServiceName } from "@/api/revenue";
import { IEUAccountNettings, IEUAccountSendForm, IEUAccountServices, IEUAccountStats, IEUAccountStatsServices } from "@/api/types/revenue";
import { deleteEUAccount, getEUAccount, editEUAccount } from "@/api/revenue";
import EUAccountTabMain from "./ComponentTabMain.vue";
import EUAccountTabStats from "./ComponentTabStats.vue";
import BaseDialog from "@/components-UI/modals/BaseDialog.vue";
import BaseQuestionMarkTooltip from "@/components-UI/BaseQuestionMarkTooltip.vue";
import { getDefaultTrafficPeriodForMonth, trafficPeriodStringToRange } from "@/views/revenue/EUAccounts/euAccountTrafficPeriod";
import { isEuAccountPercentGoal } from "@/views/revenue/EUAccounts/euAccountGoalRevenue";
import { isFilledEuAccountStat } from '@/views/revenue/EUAccounts/euAccountStatsPayload';
import { isAdvertiserSendingInvoicesTo1C } from '@/services/advertiser/advertiserSendsInvoicesTo1C';


export default /*#__PURE__*/_defineComponent({
  __name: 'EUAccountSingleAndCreate',
  setup(__props) {

const getServicePayoutType = (el: IEUAccountServices): 'fixed' | 'percent' =>
    (isEuAccountPercentGoal(el) ? 'percent' : 'fixed');

/**
 * Название услуги для payable_services.name — именно оно уходит в 1С.
 * Отправляем ровно то, что стоит в поле «Услуга»: подставлять вместо пустого
 * значения склейку оффера и цели нельзя — иначе в счёте окажется название,
 * которого менеджер не вводил, и после перезагрузки оно вернётся из бэка.
 */
const getPayableServiceName = (el: IEUAccountServices): string => el.service_name?.trim() ?? '';

const updateChangedServiceNames = async (services: IEUAccountServices[]): Promise<void> => {
    const changed = services.filter(
        (el) => el.service_name !== undefined && el.service_name !== el.original_service_name,
    );
    await Promise.allSettled(
        changed.map((el) => updateGoalServiceName(el.offer_id, el.goal_id, el.service_name!)),
    );
};

const EUAccountsStore = useEUAccountsStore();
const {
    form,
    isMainFormComplete,
    currentRadioValue,
    selectedAdvertiser,
    EUAccountObj,
    isEdit,
    isMainFormFilled,
    servicesToPay,
    isServicesToPayDifferenceMoreThanOnePercent,
} = storeToRefs(EUAccountsStore);
const {
    clearAll,
    setDefaultData,
} = EUAccountsStore;

const currentTab = ref<string>(router.currentRoute.query?.tab);
const tabs: { text: string, value: string, disabled?: boolean }[] = [
    { text: 'Основное', value: 'main' },
    { text: 'Статистика', value: 'stats', disabled: false },
];
const tabComponent: { main: Component, stats: Component } = {
    main: EUAccountTabMain,
    stats: EUAccountTabStats,
};
const isLeaveThisPage = ref(false);
const isDeleteDialog = ref(false);
const isFooterActionLoading = ref(false);
const pathName = ref('');
const dontTouch = ref(false);
const pendingNext = ref<((to?: unknown) => void) | null>(null);

const currentTabComponent = computed<Component>(() => {
    return tabComponent[currentTab.value as keyof typeof tabComponent] || tabComponent.main;
});

const getStatus = computed<IStatus>(() => {
    return translateEUAccountStatus(EUAccountObj.value?.status || '');
});

const getPageLayoutBind = computed<{ title: string, sticky: string }>(() => {
    if (isSingle.value) {
        return {
            title: `Счёт №${EUAccountObj.value?.id}, ${EUAccountObj.value?.advertiser?.name}`,
            sticky: 'footer',
            class: ['--is-single'],
        };
    }

    return {
        title: 'Создание счёта',
        sticky: 'footer, header',
        class: ['--is-create'],
    };
});

const isSingle = computed(() => router.currentRoute.params.id !== 'create');

const isAdvertiserSendingTo1C = computed(() => isAdvertiserSendingInvoicesTo1C(selectedAdvertiser.value));

// При создании переключатель берёт значение каждого нового рекламодателя: ручное выключение при смене сбрасывается
watch(selectedAdvertiser, () => {
    if (isSingle.value) return;
    form.value.send_to_1c = isAdvertiserSendingTo1C.value;
});

const isEditAllowedByRole = computed(() => {
    const currentUser = store.getters['authModule/GET_USER'];
    if (!currentUser || !currentUser.roles || currentUser.roles.length === 0) {
        return false;
    }

    const userRoles = currentUser.roles as string[];
    const userAccountId = currentUser.account_id;

    if (userRoles.includes(USER_ROLES.ADMIN) || userRoles.includes(USER_ROLES.SUPERVISOR)) {
        return true;
    }

    if (!userRoles.includes(USER_ROLES.ACCOUNT_MANAGER)) {
        return false;
    }

    if (!selectedAdvertiser.value || !userAccountId) {
        return false;
    }

    const isAccountManager = selectedAdvertiser.value.account_manager?.account_id === userAccountId;
    const isAdditionalManager = selectedAdvertiser.value.managers?.some((manager) => manager.manager_id === userAccountId) || false;

    return isAccountManager || isAdditionalManager;
});

const isAllDisabled = computed(() => {
    const currentUser = store.getters['authModule/GET_USER'];

    if (!currentUser || !currentUser.roles || currentUser.roles.length === 0) {
        return {
            isAccManager: false,
            isNotPermission: true,
        };
    }

    const userRoles = currentUser.roles as string[];
    const userAccountId = currentUser.account_id;

    const hasBasicPermission = userRoles.includes(USER_ROLES.ADMIN) ||
        userRoles.includes(USER_ROLES.SUPERVISOR) ||
            userRoles.includes(USER_ROLES.ACCOUNT_MANAGER);

    let isAccManagerBlocked = false;
    if (userRoles.includes(USER_ROLES.ACCOUNT_MANAGER)) {
        if (!selectedAdvertiser.value || !userAccountId) {
            isAccManagerBlocked = isSingle.value;
        } else {
            const isAccountManager = selectedAdvertiser.value.account_manager?.account_id === userAccountId;
            const isAdditionalManager = selectedAdvertiser.value.managers?.some((manager) => manager.manager_id === userAccountId) || false;

            isAccManagerBlocked = !(isAccountManager || isAdditionalManager);
        }
    }

    return {
        isAccManager: isAccManagerBlocked,
        isNotPermission: !hasBasicPermission,
    };
});

const SERVICES_TO_PAY_DIFFERENCE_ERROR =
    'Сумма к оплате отличается от целевой суммы более, чем на 1%. Возможно только сохранение как черновик.';
const PAYABLE_UNIT_ERROR =
    'Для каждой услуги к оплате нужно выбрать «Тип цели».';
const PAYABLE_NAME_ERROR =
    'Для каждой услуги к оплате нужно указать название.';
const SEND_TO_ADVERTISER_TOOLTIP =
    'Если включено, то как только из бухгалтерии будет получен файл инвойса, он будет сразу отправлен рекламодателю.';

const getAccountTrafficPeriod = (): { from: string | null; to: string | null } => {
    const row = servicesToPay.value.find((el) => el.traffic_period_from && el.traffic_period_to);
    if (row?.traffic_period_from && row?.traffic_period_to) {
        return { from: row.traffic_period_from, to: row.traffic_period_to };
    }
    return trafficPeriodStringToRange(getDefaultTrafficPeriodForMonth(form.value.period));
};

const isPayableTrafficInvalid = computed(() =>
    servicesToPay.value.some((el) => !el.traffic_period_from || !el.traffic_period_to),
);

/**
 * «Тип цели» обязателен для каждой услуги к оплате — он уходит в 1С.
 * Проверка не зависит от черновика: в `payable_services[].unit` не должно
 * попадать пустое значение, а единственный способ его заполнить — выбор в поле.
 */
const isPayableUnitInvalid = computed(() =>
    servicesToPay.value.some((el) => !el.unit),
);

/**
 * Название услуги к оплате обязательно: оно уходит в `payable_services[].name`
 * и попадает в счёт рекламодателю. Пустое поле оставляет в счёте безымянную
 * строку, поэтому проверка, как и «Тип цели», не зависит от черновика.
 */
const isPayableNameInvalid = computed(() =>
    servicesToPay.value.some((el) => !getPayableServiceName(el)),
);

const isDisabledSaveButton = computed(() => {
    if (isAllDisabled.value.isAccManager || isAllDisabled.value.isNotPermission) {
        return true;
    }

    if (!form.value.is_draft && isServicesToPayDifferenceMoreThanOnePercent.value) {
        return true;
    }

    if (servicesToPay.value.length > 0 && isPayableUnitInvalid.value) {
        return true;
    }

    if (servicesToPay.value.length > 0 && isPayableNameInvalid.value) {
        return true;
    }

    if (!form.value.is_draft && servicesToPay.value.length > 0 && isPayableTrafficInvalid.value) {
        return true;
    }

    return !form.value.is_draft ? !isMainFormComplete.value ||
        !(
            form.value.services.length &&
                form.value.services.every((el) =>
                    isEuAccountPercentGoal(el) ?
                        el.original_amount : form.value.currency !== el.goal_currency ?
                            el.original_amount && el.exchange_rate : el.original_amount,
                )
        ) ||
        (
            currentRadioValue.value === '2' ?
                !form.value.netting_act_amount : currentRadioValue.value === '3' ?
                    !(
                        form.value.nettings.length &&
                            form.value.nettings.every(el =>
                                form.value.currency !== el.currency ? el.final_amount && el.exchange_rate : el.final_amount,
                            ))
                    : false
        ) : false;
});

const alertErrors = computed(() => {
    const errors: string[] = [];

    if (isAllDisabled.value.isAccManager || isAllDisabled.value.isNotPermission) {
        if (isAllDisabled.value.isAccManager) {
            errors.push('Вы не можете создавать счета данному рекламодателю.');
        }
        if (isAllDisabled.value.isNotPermission) {
            errors.push('У вас не хватает прав на создание счетов рекламодателям.');
        }
    } else {
        if (servicesToPay.value.length > 0 && isPayableUnitInvalid.value) {
            errors.push(PAYABLE_UNIT_ERROR);
        } else if (servicesToPay.value.length > 0 && isPayableNameInvalid.value) {
            errors.push(PAYABLE_NAME_ERROR);
        } else if (!form.value.is_draft && isServicesToPayDifferenceMoreThanOnePercent.value) {
            errors.push(SERVICES_TO_PAY_DIFFERENCE_ERROR);
        } else if (isDisabledSaveButton.value) {
            errors.push('Не заполнены обязательные поля.');
        }
    }

    return errors.join(' ');
});

const EDITABLE_EU_ACCOUNT_STATUSES: string[] = ['new', 'draft', 'sent_to_1c'];

const isEditAllowedByStatus = computed(() => {
    const status = EUAccountObj.value?.status;
    return status ? EDITABLE_EU_ACCOUNT_STATUSES.includes(status) && isEditAllowedByRole.value : false;
});

const isAccountDeleted = computed(() => EUAccountObj.value?.status === 'deleted');

const getSingleId = computed(() => router.currentRoute.params.id);

const changeTab = (event: string): void => {
    dontTouch.value = true;
    currentTab.value = event;
    router.push({
        name: 'eu-invoice',
        params: { id: router.currentRoute.params.id },
        query: { tab: event },
    });
    dontTouch.value = false;
};

const handleSave = async (): Promise<void> => {
    if (servicesToPay.value.length > 0 && isPayableUnitInvalid.value) {
        showNotification(PAYABLE_UNIT_ERROR, 'info');
        return;
    }

    if (servicesToPay.value.length > 0 && isPayableNameInvalid.value) {
        showNotification(PAYABLE_NAME_ERROR, 'info');
        return;
    }

    if (!form.value.is_draft && isServicesToPayDifferenceMoreThanOnePercent.value) {
        return;
    }

    try {
        isFooterActionLoading.value = true;

        const cleanedStats = form.value.stats
            ?.filter((stat: IEUAccountStats) => isFilledEuAccountStat(stat))
            .map((stat: IEUAccountStats) => ({
                id: stat.id,
                file: stat.file,
                comment: stat.comment,
                services: stat.services.map(e => ({
                    final_rate: Math.round(Number(e.final_rate ?? 0)),
                    start_rate: Math.round(Number(e.start_rate ?? 0)),
                    goal_id: e.goal_id,
                    offer_id: e.offer_id,
                    quantity: e.quantity,
                    offer_name: e.offer_name,
                    goal_name: e.goal_name,
                })),
            })) || [];
        const accountTp = getAccountTrafficPeriod();

        const mapServiceToPayload = (el: IEUAccountServices) => ({
            offer_id: el.offer_id,
            offer_name: el.offer_name,
            goal_id: el.goal_id,
            goal_name: el.goal_name,
            quantity: el.quantity,
            original_amount: (el.original_amount as number).toFixed(2),
            final_amount: (el.final_amount as number).toFixed(2),
            exchange_rate: (el.exchange_rate as number).toFixed(4),
            currency: el.goal_currency,
            operation: el.operation || 'default',
            payout_type: getServicePayoutType(el),
        });

        const mapPayableServiceToPayload = (el: IEUAccountServices) => ({
            offer_id: el.offer_id,
            goal_id: el.goal_id,
            name: getPayableServiceName(el),
            quantity: el.quantity,
            original_amount: (el.original_amount as number).toFixed(2),
            final_amount: (el.final_amount as number).toFixed(2),
            exchange_rate: (el.exchange_rate as number).toFixed(4),
            currency: el.goal_currency,
            operation: el.operation || 'default',
            payout_type: getServicePayoutType(el),
            // Строго выбранное значение: пустой unit до отправки не доходит,
            // его блокирует isPayableUnitInvalid — null сюда попасть не должен
            unit: el.unit as string,
            traffic_period_from: el.traffic_period_from ?? null,
            traffic_period_to: el.traffic_period_to ?? null,
        });

        const payload: IEUAccountSendForm = {
            advertiser_id: form.value.advertiser_id,
            currency: form.value.currency,
            period: `${form.value.period}-01`,
            traffic_period_from: accountTp.from,
            traffic_period_to: accountTp.to,
            legal_entity_id: form.value.legal_entity_id,
            contractor_id: form.value.contractor_id,
            project: form.value.project,
            days_to_pay: form.value.days_to_pay,
            auto_email: !!form.value.auto_email,
            is_draft: form.value.is_draft,
            send_to_1c: !!form.value.send_to_1c,
            comment: form.value.comment,
            netting_act_amount: currentRadioValue.value === '2' ? form.value.netting_act_amount : null,
            stats: cleanedStats,
            services: form.value.services.map(mapServiceToPayload),
            payable_services: servicesToPay.value.map(mapPayableServiceToPayload),
            nettings: form.value.nettings?.map((el: IEUAccountNettings) => ({
                affiliate_invoice_id: el.affiliate_invoice_id,
                currency: el.currency,
                exchange_rate: (el.exchange_rate as number).toFixed(4),
                original_amount: (el.original_amount as number).toFixed(2),
                final_amount: (el.final_amount as number).toFixed(2),
                operation: el.operation || 'default',
            })),
        };

        await createEUAccount(payload);
        await updateChangedServiceNames(servicesToPay.value);
        showNotification('Счёт успешно сохранён', 'success');
        leaveThisPage('eu-invoices');
    } catch (err) {
        showServerError(err, 'Ошибка при сохранении счёта');
    } finally {
        isFooterActionLoading.value = false;
    }
};

const handleClickLeftButton = async (): Promise<void> => {
    setDefaultData();

    if (isEdit.value) {
        isEdit.value = false;

        return;
    }

    if (isSingle.value && !isEditAllowedByRole.value) {
        showNotification('Недостаточно прав для редактирования этого счёта', 'info');
        return;
    }

    isEdit.value = true;
};

const handleClickRightButton = async (): Promise<void> => {
    if (!isEdit.value) {
        isDeleteDialog.value = true;
        return;
    }

    if (servicesToPay.value.length > 0 && isPayableUnitInvalid.value) {
        showNotification(PAYABLE_UNIT_ERROR, 'info');
        return;
    }

    if (servicesToPay.value.length > 0 && isPayableNameInvalid.value) {
        showNotification(PAYABLE_NAME_ERROR, 'info');
        return;
    }

    if (!form.value.is_draft && isServicesToPayDifferenceMoreThanOnePercent.value) {
        showNotification(SERVICES_TO_PAY_DIFFERENCE_ERROR, 'info');
        return;
    }

    try {
        isFooterActionLoading.value = true;
        const mapServiceToPayload = (el: IEUAccountServices) => ({
            offer_id: el.offer_id,
            offer_name: el.offer_name,
            goal_id: el.goal_id,
            goal_name: el.goal_name,
            quantity: el.quantity,
            original_amount: Number(Number(el.original_amount ?? 0).toFixed(2)),
            final_amount: Number(Number(el.final_amount ?? 0).toFixed(2)),
            exchange_rate: Number(Number(el.exchange_rate ?? 0).toFixed(4)),
            currency: el.currency || el.goal_currency,
            operation: el.operation || 'default',
            payout_type: getServicePayoutType(el),
        });

        const mapPayableServiceToPayload = (el: IEUAccountServices) => ({
            offer_id: el.offer_id,
            goal_id: el.goal_id,
            name: getPayableServiceName(el),
            quantity: el.quantity,
            original_amount: Number(Number(el.original_amount ?? 0).toFixed(2)),
            final_amount: Number(Number(el.final_amount ?? 0).toFixed(2)),
            exchange_rate: Number(Number(el.exchange_rate ?? 0).toFixed(4)),
            currency: el.currency || el.goal_currency,
            operation: el.operation || 'default',
            payout_type: getServicePayoutType(el),
            // Строго выбранное значение: пустой unit до отправки не доходит,
            // его блокирует isPayableUnitInvalid — null сюда попасть не должен
            unit: el.unit as string,
            traffic_period_from: el.traffic_period_from ?? null,
            traffic_period_to: el.traffic_period_to ?? null,
        });

        const accountTp = getAccountTrafficPeriod();
        const payload: IEUAccountSendForm = {
            services: form.value.services.map(mapServiceToPayload),
            payable_services: servicesToPay.value.map(mapPayableServiceToPayload),
            traffic_period_from: accountTp.from,
            traffic_period_to: accountTp.to,
            nettings: !form.value.nettings.length ? null : form.value.nettings.map((el: IEUAccountNettings) => ({
                affiliate_invoice_id: el.affiliate_invoice_id,
                currency: el.currency,
                original_amount: Number(Number(el.original_amount ?? 0).toFixed(2)),
                final_amount: Number(Number(el.final_amount ?? 0).toFixed(2)),
                exchange_rate: Number(Number(el.exchange_rate ?? 0).toFixed(2)),
                operation: el.operation || 'default',
            })),
        };

        if (form.value.period !== EUAccountObj.value.period) payload.period = `${form.value.period}-01`;
        if (form.value.comment !== EUAccountObj.value.comment) payload.comment = form.value.comment;
        if (form.value.currency !== EUAccountObj.value.currency) payload.currency = form.value.currency;
        if (form.value.legal_entity_id !== EUAccountObj.value.legal.id) payload.legal_entity_id = form.value.legal_entity_id;
        if (form.value.contractor_id !== EUAccountObj.value.contractor.id) payload.contractor_id = form.value.contractor_id;
        if (form.value.project !== EUAccountObj.value.project) payload.project = form.value.project;
        if (form.value.days_to_pay !== EUAccountObj.value.days_to_pay) payload.days_to_pay = form.value.days_to_pay;
        if (!!form.value.auto_email !== !!EUAccountObj.value.auto_email) {
            payload.auto_email = !!form.value.auto_email;
        }
        // is_draft уходит всегда, а не только при смене: бэк по отсутствию ключа
        // не понимает, каким счёт должен остаться. Приведение нужно, чтобы в теле
        // оказался именно true или false, а не undefined
        payload.is_draft = !!form.value.is_draft;

        payload.netting_act_amount = currentRadioValue.value === '2' ? form.value.netting_act_amount : null;

        const filledStats = (form.value.stats ?? [])
            .filter((stat: IEUAccountStats) => isFilledEuAccountStat(stat))
            .map((stat: IEUAccountStats) => {
                const statWithComments = stat as IEUAccountStats & { comments?: Array<{ id?: number; text: string }> };
                return {
                    id: stat.id ?? 0,
                    file: stat.file ?? null,
                    services: stat.services.map((e: IEUAccountStatsServices) => ({
                        id: e.id ?? 0,
                        offer_id: e.offer_id,
                        goal_id: e.goal_id,
                        start_rate: Math.round(Number(e.start_rate ?? 0)),
                        final_rate: Math.round(Number(e.final_rate ?? 0)),
                        quantity: e.quantity,
                        offer_name: e.offer_name,
                        goal_name: e.goal_name,
                    })),
                    comments: (statWithComments.comments ?? []).map((c) => ({
                        id: c.id ?? 0,
                        text: c.text,
                    })),
                };
            });

        // Бэк заводит задачу на статистику по самому факту наличия ключа `stats`,
        // даже если массив пустой. Ключ уходит только когда есть что сохранять
        // либо когда у счёта уже была статистика — иначе её удаление не дойдёт.
        if (filledStats.length > 0 || !!EUAccountObj.value?.stats) {
            payload.stats = filledStats;
        }

        await editEUAccount(getSingleId.value, payload);
        await updateChangedServiceNames(servicesToPay.value);
        await fetchSinglePageEUAccount();
    } catch (e) {
        showServerError(e, 'Не удалось отредактировать счет');
        setDefaultData();
    } finally {
        isFooterActionLoading.value = false;
    }

    isEdit.value = false;
};

const handleDeleteAccount = async (): Promise<void> => {
    try {
        isDeleteDialog.value = false;
        dontTouch.value = true;
        isFooterActionLoading.value = true;
        await deleteEUAccount(getSingleId.value);
        showNotification('Счёт успешно удалён', 'info');
        router.push({ name: 'eu-invoices' });
    } catch (e) {
        showServerError(e, 'Не удалось удалить счет');
    } finally {
        isFooterActionLoading.value = false;
    }
};

const handleStayOnPage = (): void => {
    isLeaveThisPage.value = false;
    dontTouch.value = false;
    if (pendingNext.value) {
        pendingNext.value(false);
        pendingNext.value = null;
    }
};

const handleLeavePage = (): void => {
    isLeaveThisPage.value = false;
    const routeName = pathName.value || 'eu-invoices';
    if (pendingNext.value) {
        clearAll();
        dontTouch.value = true;
        isMainFormFilled.value = false;
        pendingNext.value({ name: routeName });
        pendingNext.value = null;
    } else {
        leaveThisPage(routeName);
    }
};

const updatePageTitle = (): void => {
    if (EUAccountObj.value?.advertiser?.name && EUAccountObj.value?.id) {
        document.title = `${EUAccountObj.value.advertiser.name} ${EUAccountObj.value.id}`;
    }
};

const fetchSinglePageEUAccount = async (): Promise<void> => {
    try {
        const resp = await getEUAccount(getSingleId.value);
        EUAccountObj.value = resp.data;
        setDefaultData();
        updatePageTitle();
    } catch (e) {
        showServerError(e, 'Не удалось получить детальную информацию');
    }
};

const handleBackButton = (): void => {
    if (isSingle.value && isEdit.value && !dontTouch.value) {
        isLeaveThisPage.value = true;
        pathName.value = 'eu-invoices';
        updatePageTitle();
    } else if (!isSingle.value && !dontTouch.value) {
        isLeaveThisPage.value = true;
        pathName.value = 'eu-invoices';
        document.title = 'Создание счёта';
    } else {
        dontTouch.value = true;
        leaveThisPage('eu-invoices');
    }
};

const leaveThisPage = (name?: string): void => {
    clearAll();
    dontTouch.value = true;
    isMainFormFilled.value = false;
    isLeaveThisPage.value = false;
    const routeName = name || pathName.value || 'eu-invoices';
    router.push({
        name: routeName,
    });
};

const removeBeforeEachGuard = router.beforeEach((_to, _from, next) => {
    if (_from.name === 'eu-invoice') {
        const isOnlyQueryChange = _to.name === _from.name &&
            JSON.stringify(_to.params) === JSON.stringify(_from.params) &&
            _to.params.id === _from.params.id;

        if (isOnlyQueryChange) {
            next();
            return;
        }

        const isStayingOnPage = _to.name === 'eu-invoice' && _from.params.id !== 'create' && _to.params.id !== 'create';

        if (_from.params.id !== 'create' && isEdit.value && !dontTouch.value && !isStayingOnPage) {
            isLeaveThisPage.value = true;
            pathName.value = _to.name || 'eu-invoices';
            pendingNext.value = next;
            updatePageTitle();
            return;
        }

        if (_from.params.id !== 'create' && _to.params.id === 'create') {
            window.location.href = _to.fullPath;
            return;
        }

        if (_from.params.id === 'create' && !dontTouch.value) {
            isLeaveThisPage.value = true;
            pathName.value = _to.name || 'eu-invoices';
            pendingNext.value = next;
            document.title = 'Создание счёта';
            return;
        }

        next();
    } else {
        next();
    }
});

onBeforeUnmount(() => {
    removeBeforeEachGuard();
    if (pendingNext.value) {
        pendingNext.value(false);
        pendingNext.value = null;
    }
});

const syncModeFromRoute = async (): Promise<void> => {
    const routeId = router.currentRoute.params.id;
    if (routeId && routeId !== 'create') {
        isEdit.value = false;
        dontTouch.value = false;
        await fetchSinglePageEUAccount();
    } else if (routeId === 'create') {
        clearAll();
        isEdit.value = true;
        dontTouch.value = false;
    }
};

watch(
    () => router.currentRoute.params.id,
    syncModeFromRoute,
    { immediate: true },
);

return { __sfc: true,getServicePayoutType, getPayableServiceName, updateChangedServiceNames, EUAccountsStore, form, isMainFormComplete, currentRadioValue, selectedAdvertiser, EUAccountObj, isEdit, isMainFormFilled, servicesToPay, isServicesToPayDifferenceMoreThanOnePercent, clearAll, setDefaultData, currentTab, tabs, tabComponent, isLeaveThisPage, isDeleteDialog, isFooterActionLoading, pathName, dontTouch, pendingNext, currentTabComponent, getStatus, getPageLayoutBind, isSingle, isAdvertiserSendingTo1C, isEditAllowedByRole, isAllDisabled, SERVICES_TO_PAY_DIFFERENCE_ERROR, PAYABLE_UNIT_ERROR, PAYABLE_NAME_ERROR, SEND_TO_ADVERTISER_TOOLTIP, getAccountTrafficPeriod, isPayableTrafficInvalid, isPayableUnitInvalid, isPayableNameInvalid, isDisabledSaveButton, alertErrors, EDITABLE_EU_ACCOUNT_STATUSES, isEditAllowedByStatus, isAccountDeleted, getSingleId, changeTab, handleSave, handleClickLeftButton, handleClickRightButton, handleDeleteAccount, handleStayOnPage, handleLeavePage, updatePageTitle, fetchSinglePageEUAccount, handleBackButton, leaveThisPage, removeBeforeEachGuard, syncModeFromRoute, BasePageLayout, BasePageTabs, BaseStatus, BaseButton, BaseSwitch, SvgReload, SvgTrash, SvgPencil, SvgCancel, SvgCheck, BaseAlert, BaseDialog, BaseQuestionMarkTooltip }
}

})